import { useTranslation } from 'i18n';
import { toast } from 'services/toast';
import { cutSurfaceNodesMutation } from 'graphql/surface-node/mutations/cut-surface-nodes';
import useMutation from 'hooks/use-mutation';
import useSaveNodesInCache from 'components/canvas/hooks/use-save-nodes-in-cache';
import useFindNodeInCache from 'components/canvas/hooks/use-find-node';
import Response from 'services/graphql/response';
import ResponseError from 'services/graphql/response-error';
import { Position } from 'types';

interface CutSurfaceNodesInput {
  id: string;
  surface?: string;
  position?: Position;
}

export default function useCutSurfaceNodes(
  surfaceId: string,
): (
  input: CutSurfaceNodesInput[],
) => Promise<any | Response<any> | ResponseError<Error>> {
  const { t } = useTranslation();

  const saveNodesInCache = useSaveNodesInCache(surfaceId);
  const { findNodeById } = useFindNodeInCache(surfaceId);

  const [cutSurfaceNodes] = useMutation(cutSurfaceNodesMutation, {});

  return function (input: CutSurfaceNodesInput[]) {
    const isCutOnSameSurface = input[0].surface === surfaceId;

    return cutSurfaceNodes({ variables: { input } }).then((response) => {
      if (response.isFailure) {
        return toast.error(response.getErrorMessage());
      }

      if (isCutOnSameSurface) {
        const cutNodes = response.data;

        const containingNodes = cutNodes
          .map((node) => node.contains)
          .filter(Boolean);

        saveNodesInCache((nodes) => [
          ...nodes,
          ...containingNodes,
          ...cutNodes,
        ]);
      }

      toast.success(
        t(`Successfully cut nodes {{names}}`, {
          replace: {
            names:
              input
                .map(({ id }) => findNodeById(id))
                .map((node) => node?.name)
                .join(', ') || '',
          },
        }),
      );

      return response;
    });
  };
}
